<template>
  <div class="d-flex flex-column" style="gap: 20px">
    <span class="text-h6">{{ $t('packageResults.overviewByCat') }}</span>

    <v-tabs v-model="tabIndex" color="#1b2643">
      <v-tab tab-value="overall">{{ $t('packageResults.overall') }}</v-tab>
      <v-tab tab-value="range">{{ $t('packageResults.range') }}</v-tab>
      <v-tab tab-value="accuracy">{{ $t('packageResults.accuracy') }}</v-tab>
      <v-tab tab-value="phonology">{{ $t('packageResults.phonology') }}</v-tab>
      <v-tab tab-value="fluency">{{ $t('packageResults.fluency') }}</v-tab>
      <v-tab tab-value="coherence">{{ $t('packageResults.coherence') }}</v-tab>
      <v-tab tab-value="interaction">
        {{ $t('packageResults.interaction') }}
      </v-tab>
    </v-tabs>

    <template v-if="loading">
      <v-card :loading="loading">
        <div style="padding: 16px">
          {{ $t('packageResults.loading') }}
        </div>
      </v-card>
    </template>
    <v-simple-table v-else dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="sticky-header" style="width: 120px" />
            <template v-for="item in data">
              <th
                v-if="speakingTestOnly ? item.scenarioShouldAssess : true"
                :key="item.campaignId"
                style="width: 300px; min-width: 300px"
              >
                {{ item.campaign?.name }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="level in cefrLevels" :key="level">
            <th class="sticky-header" style="width: 300px; min-width: 300px">
              <strong>{{ level }}</strong>
            </th>
            <template v-for="(value, i) in tableData(level)">
              <td v-if="value !== 'HIDE'" :key="i">
                {{ value }}
              </td>
            </template>
          </tr>
          <tr>
            <th class="sticky-header">
              <strong>{{ $t('packageResults.avgScore') }}</strong>
            </th>
            <template v-for="(value, i) in averageTableData()">
              <td v-if="value !== 'HIDE'" :key="i">
                {{ value }}
              </td>
            </template>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <template v-if="loading">
      <v-card :loading="loading">
        <div style="padding: 16px">
          {{ $t('packageResults.loading') }}
        </div>
      </v-card>
    </template>
    <v-data-table
      v-else
      ref="dataTable"
      :headers="headers"
      :items="tableItems"
      :page="currentPage"
      locale="ja"
      class="elevation-0 freeze-columns"
      dense
      :footer-props="{
        'items-per-page-options': itemsPerPageOptions,
      }"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="sticky-header" style="width: 150px">
            {{ item.displayName }}
          </td>
          <td>{{ item.couponCode }}</td>
          <td>{{ formatUseCount(item.useCount) }}</td>
          <template v-for="(cell, i) in item.campaignCells">
            <overview-coupon-column
              v-if="cell.row"
              :key="`cell-${i}`"
              :item="cell.row"
              :tab-index="tabIndex"
              :coupon-code="cell.row.couponCode"
            />
            <td v-else :key="`empty-${i}`" style="width: 300px">ー</td>
          </template>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import type Vue from "vue";
import type { CefrResults } from "@/resources/dialog";
import type { CampaignResults } from "@/resources/package";
import { compareCouponsByCode } from "@/utils/getCoupons";
import OverviewCouponColumn from "./OverviewCouponColumn.vue";

type Header = {
  text: string;
  value: string;
  width: number;
  class?: string;
};
type CampaignCell = {
  campaignId: string;
  row: CampaignResults["rows"][0] | null;
};
type CampaignRow = {
  displayName: string;
  couponCode: string;
  useCount: number;
  campaignCells: CampaignCell[];
};

export default defineComponent({
  name: "OverviewByCategory",

  components: {
    OverviewCouponColumn,
  },

  props: {
    data: {
      type: Array as () => CampaignResults[],
      required: true,
    },
    speakingTestOnly: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tabIndex: "overall",
      cefrLevels: ["Pre-A1", "A1", "A2", "B1", "B2", "C1", "C2"],
      currentPage: 1,
      itemsPerPage: 100,
      itemsPerPageOptions: [100, 300, 1000, 5000, -1],
    };
  },

  computed: {
    visibleCampaigns(): CampaignResults[] {
      return this.data.filter(
        (item) => !this.speakingTestOnly || item.scenarioShouldAssess,
      );
    },
    headers(): Header[] {
      const baseHeaders = [
        {
          text: this.$t("packageResults.userDisplayName") as string,
          value: "displayName",
          width: 150,
        },
        {
          text: this.$t("packageResults.coupon") as string,
          value: "couponCode",
          width: 150,
        },
        {
          text: this.$t("packageResults.use") as string,
          value: "useCount",
          width: 80,
        },
      ];

      const campaignHeaders = this.visibleCampaigns.map((item) => ({
        text: item.campaign?.name || "",
        value: `campaign_${item.campaignId}`,
        width: 300,
      }));

      return [...baseHeaders, ...campaignHeaders];
    },
    tableItems(): CampaignRow[] {
      type TicketBucket = {
        displayName: string;
        couponCode: string;
        perCampaign: Map<
          string,
          {
            dialogRows: CampaignResults["rows"][0][];
            syntheticRow: CampaignResults["rows"][0] | null;
          }
        >;
      };

      const tickets = new Map<string, TicketBucket>();

      for (const campaign of this.visibleCampaigns) {
        for (const row of campaign.rows) {
          let ticket = tickets.get(row.couponCode);
          if (!ticket) {
            ticket = {
              displayName: row.displayName || "-",
              couponCode: row.couponCode,
              perCampaign: new Map(),
            };
            tickets.set(row.couponCode, ticket);
          } else if (
            (ticket.displayName === "-" || ticket.displayName === "") &&
            row.displayName
          ) {
            ticket.displayName = row.displayName;
          }

          let perCampaign = ticket.perCampaign.get(row.campaignId);
          if (!perCampaign) {
            perCampaign = { dialogRows: [], syntheticRow: null };
            ticket.perCampaign.set(row.campaignId, perCampaign);
          }

          if (row.dialogId) {
            perCampaign.dialogRows.push(row);
          } else {
            perCampaign.syntheticRow = row;
          }
        }
      }

      // Real dialogs are never capped by `campaign.limits` — historical
      // assessments must remain visible even if an admin later reduces the
      // limit. The cap is only applied to synthetic "Registered" rows below.
      const effectiveDialogs = (
        dialogRows: CampaignResults["rows"][0][],
      ): CampaignResults["rows"][0][] => dialogRows;

      const result: CampaignRow[] = [];

      const deriveRegisteredFrom = (
        sourceRow: CampaignResults["rows"][0],
      ): CampaignResults["rows"][0] => ({
        ...sourceRow,
        dialogId: "",
        status: "Registered",
        startedAt: "",
        completedAt: "",
        duration: "",
        overall: "",
        range: "",
        accuracy: "",
        phonology: "",
        fluency: "",
        coherence: "",
        interaction: "",
        score: undefined,
        canDo: undefined,
        review: undefined,
      });

      const sortedTickets = Array.from(tickets.values()).sort(
        compareCouponsByCode,
      );

      for (const ticket of sortedTickets) {
        const perCampaignEffective = this.visibleCampaigns.map((campaign) => {
          const bucket = ticket.perCampaign.get(campaign.campaignId);
          const dialogs = bucket ? effectiveDialogs(bucket.dialogRows) : [];
          const syntheticRow =
            bucket?.syntheticRow ??
            (bucket && bucket.dialogRows.length > 0
              ? deriveRegisteredFrom(bucket.dialogRows[0])
              : null);
          return {
            campaign,
            dialogs,
            syntheticRow,
          };
        });

        const maxUses = Math.max(
          1,
          ...perCampaignEffective.map((c) => c.dialogs.length),
        );

        for (let useCount = 1; useCount <= maxUses; useCount++) {
          const campaignCells: CampaignCell[] = perCampaignEffective.map(
            (entry) => {
              // Real dialogs win unconditionally — even if `useCount` is
              // beyond the campaign's currently-configured limit (e.g. the
              // admin reduced the limit after attempts were recorded).
              if (entry.dialogs.length >= useCount) {
                return {
                  campaignId: entry.campaign.campaignId,
                  row: entry.dialogs[useCount - 1],
                };
              }
              const cap = entry.campaign.campaign?.isUnlimited
                ? Number.POSITIVE_INFINITY
                : (entry.campaign.campaign?.limits ?? 0);
              if (useCount > cap) {
                return {
                  campaignId: entry.campaign.campaignId,
                  row: null,
                };
              }
              if (entry.syntheticRow) {
                return {
                  campaignId: entry.campaign.campaignId,
                  row: entry.syntheticRow,
                };
              }
              return {
                campaignId: entry.campaign.campaignId,
                row: null,
              };
            },
          );

          result.push({
            displayName: ticket.displayName,
            couponCode: ticket.couponCode,
            useCount,
            campaignCells,
          });
        }
      }

      return result;
    },
  },

  methods: {
    getCampaignRows(campaignId: string) {
      return this.data.find((d) => d.campaignId === campaignId)?.rows;
    },
    formatUseCount(count: number): string {
      if (this.$i18n.locale === "ja") {
        return `${count}回`;
      }
      const suffixes: Record<Intl.LDMLPluralRule, string> = {
        one: "st",
        two: "nd",
        few: "rd",
        other: "th",
        zero: "th",
        many: "th",
      };
      const rule = new Intl.PluralRules("en", { type: "ordinal" }).select(
        count,
      );
      return `${count}${suffixes[rule]}`;
    },
    tableData(level: string) {
      let results = [];

      for (const d of this.data) {
        let count = 0;
        const shouldShow = this.speakingTestOnly
          ? d.scenarioShouldAssess
          : true;
        if (!shouldShow) {
          results.push("HIDE");
          continue;
        }

        for (const r of d.rows) {
          const score = r.score?.[this.tabIndex as keyof CefrResults];

          if ((score?.cefrLevel || score?.label) === level) {
            count += 1;
          }
        }

        results.push(count);
      }

      return results;
    },
    averageTableData() {
      let averages = [];

      for (const d of this.data) {
        let totalScore = 0;

        const shouldShow = this.speakingTestOnly
          ? d.scenarioShouldAssess
          : true;
        if (!shouldShow) {
          averages.push("HIDE");
          continue;
        }

        for (const r of d.rows) {
          const score = r.score?.[this.tabIndex as keyof CefrResults];
          if (score) {
            totalScore += score.score;
          }
        }
        const avg = totalScore / d.rows.length;
        averages.push(window.isNaN(avg) ? "0" : avg.toFixed(2));
      }

      return averages;
    },
    /**
     * Adds a scroll event listener to handle fixed columns.
     */
    addScrollListener() {
      const dataTableRef = this.$refs.dataTable as Vue;

      if (dataTableRef && dataTableRef.$el) {
        const tableWrapper = dataTableRef.$el.querySelector(
          ".v-data-table__wrapper",
        ) as HTMLElement;

        if (tableWrapper) {
          tableWrapper.addEventListener("scroll", this.handleScroll);
        }
      }
    },
    /**
     * Handles the scroll event to keep fixed columns in place.
     */
    handleScroll(event: Event) {
      const tableWrapper = event.target as HTMLElement;
      const firstColumnWidth =
        tableWrapper.querySelector("th:nth-child(1)")?.clientWidth || 150;
      const secondColumnWidth =
        tableWrapper.querySelector("th:nth-child(2)")?.clientWidth || 150;

      const firstColumn = tableWrapper.querySelectorAll(
        "th:nth-child(1), td:nth-child(1)",
      );
      firstColumn.forEach((column) => {
        const el = column as HTMLElement;
        el.style.position = "sticky";
        el.style.left = "0";
        el.style.background = "white";
        el.style.zIndex = "2";
      });

      const secondColumn = tableWrapper.querySelectorAll(
        "th:nth-child(2), td:nth-child(2)",
      );
      secondColumn.forEach((column) => {
        const el = column as HTMLElement;
        el.style.position = "sticky";
        el.style.left = `${firstColumnWidth}px`;
        el.style.background = "white";
        el.style.zIndex = "1";
      });

      const thirdColumn = tableWrapper.querySelectorAll(
        "th:nth-child(3), td:nth-child(3)",
      );
      thirdColumn.forEach((column) => {
        const el = column as HTMLElement;
        el.style.position = "sticky";
        el.style.left = `${firstColumnWidth + secondColumnWidth}px`;
        el.style.background = "white";
        el.style.zIndex = "1";
      });
    },
  },
  updated() {
    this.addScrollListener();
  },
  beforeDestroy() {
    const dataTableRef = this.$refs.dataTable as Vue;
    if (dataTableRef && dataTableRef.$el) {
      const tableWrapper = dataTableRef.$el.querySelector(
        ".v-data-table__wrapper",
      );
      if (tableWrapper) {
        tableWrapper.removeEventListener("scroll", this.handleScroll);
      }
    }
  },
});
</script>

<style scoped>
.sticky-header {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 1;
}

.sticky-header-no-border:nth-child(1) {
  border-right: 0;
}
</style>
